const _ = require('../../../lodash');
const BaseEntity = require('../../ddd/BaseEntity');
const DomainException = require('../../DomainException');
const InvalidOperationException = require('../../InvalidOperationException');
const getConstants = require('../../../utils/getConstants');
const constants = getConstants();
const { answerStates, fieldRequiredTypes } = constants;

class BaseEntry extends BaseEntity {
   constructor(aggregateRoot, componentData, items, entries, ruleEvaluator) {
      super(aggregateRoot);
      this.entries = entries;
      this.children = [];
      this.items = items;
      this.componentData = componentData;
      this.ruleEvaluator = ruleEvaluator;

      this._resolver = aggregateRoot.getResolver ? aggregateRoot.getResolver() : null;
   }

   createChildEntries(entryTypeLookup) {
      _.each(this.componentData.viewport, (cd) => {
         const entry = new entryTypeLookup[cd.componentType](this._aggregateRoot, cd, this.items, this.entries, this.ruleEvaluator);

         this.entries.push(entry);
         this.children.push(entry);
         entry.createChildEntries(entryTypeLookup);
      });
   }

   get idItem() {
      return this.componentData.idItem ? this.componentData.idItem.toLowerCase() : null;
   }

   get item() {
      if (this.componentData.idItem == null) {
         return null;
      }
      return _.find(this.items, (i) => {
         return i.id === this.componentData.idItem;
      });
   }

   walkDescendants(visitor) {
      const stack = (this.children || []).slice().reverse();
      while (stack.length) {
         const node = stack.pop();
         visitor(node);
         if (node.children && node.children.length) {
            for (let i = node.children.length - 1; i >= 0; i--) {
               stack.push(node.children[i]);
            }
         }
      }
   }

   addComment({ idFrame, comment, actionedOn }) {
      const item = this.item;

      const noOfComments = (item.noOfComments || 0) + 1;

      const p = {
         idItem: this.idItem,
         actionedOn,
         idFrame,
         comment,
         noOfComments,
         noSubmissionItem: this.item.no,
         noSubmission: this._aggregateRoot.getSubmissionNo()
      };

      this.apply('SUBMISSION_ITEM_COMMENT_ADDED', p);

      item.noOfComments = noOfComments;
   }

   deleteComment({ idFrame, actionedOn }) {
      const item = this.item;

      let noOfComments = (item.noOfComments || 0) - 1;
      if (noOfComments < 1) {
         noOfComments = 0;
      }

      const p = {
         idItem: this.idItem,
         actionedOn,
         idFrame,
         noOfComments,
         noSubmissionItem: this.item.no,
         noSubmission: this._aggregateRoot.getSubmissionNo()
      };

      this.apply('SUBMISSION_ITEM_COMMENT_DELETED', p);

      item.noOfComments = noOfComments;
   }

   skip({ actionedOn }) {
      if (this.item.idRequiredType !== fieldRequiredTypes.SKIPABLE.id) {
         throw new DomainException('Only entries which are marked as skippable can be skipped.');
      }

      const newItemState = this.item.state | answerStates.SKIPPED;

      const p = {
         idItem: this.idItem,
         state: newItemState,
         actionedOn,
         noSubmissionItem: this.item.no,
         noSubmission: this._aggregateRoot.getSubmissionNo()
      };

      this.apply('SUBMISSION_ITEM_SKIPPED', p);

      this.item.state = newItemState;
      this.item.upd = actionedOn;
   }

   unskip({ actionedOn }) {
      if (this.item.idRequiredType !== fieldRequiredTypes.SKIPABLE.id) {
         throw new DomainException('Only entries which are marked as skippable can be unskipped.');
      }

      const newItemState = this.item.state & ~answerStates.SKIPPED;

      const p = {
         idItem: this.idItem,
         state: newItemState,
         actionedOn,
         noSubmissionItem: this.item.no,
         noSubmission: this._aggregateRoot.getSubmissionNo()
      };

      this.apply('SUBMISSION_ITEM_UNSKIPPED', p);

      this.item.state = newItemState;
      this.item.upd = actionedOn;
   }

   isOfItemType(expectedFormItemType) {
      return expectedFormItemType.id === this.item.idItemType;
   }

   ensureIsItemType(expectedFormItemType, message) {
      message = message || 'Cannot perform this operation on an item of this type.';
      if (expectedFormItemType.id !== this.item.idItemType) {
         throw new DomainException(message);
      }
   }

   getChildren() {
      return this.children;
   }

   isCurrentlyValid() {
      const item = this.item;
      if (!item) {
         return true;
      }

      return (item.state & answerStates.INVALID) !== answerStates.INVALID;
   }

   setCurrentValidity(isValid) {
      const item = this.item;
      if (!item) {
         return isValid;
      }

      const { state } = item;
      item.state = isValid ? state & ~answerStates.INVALID : state | answerStates.INVALID;
      return isValid;
   }

   areImmediateChildrenCurrentlyValid() {
      for (let i = 0; i < this.children.length; i++) {
         if (!this.children[i].isCurrentlyValid()) {
            return false;
         }
      }
      return true;
   }

   lock() {
      throw new InvalidOperationException('Subclasses must implement a lock method');
   }

   unlock() {
      throw new InvalidOperationException('Subclasses must implement a unlock method');
   }

   validate() {
      throw new InvalidOperationException('Subclasses must implement a validate method');
   }

   hasFailures() {
      const failed = false;

      if (this.item) {
         this.item.failed = failed;
      }
      return failed;
   }

   countFlags() {
      // Most entry types aren't flagged
      // Should an entry type support flagging, override this
      return {
         flagged: false,
         noOfFlags: 0
      };
   }

   score() {
      // Indicates scoring is not applicable
      // most items don't score points and don't have a max number of points
      const result = {
         score: null,
         maxScore: null
      };

      if (this.item) {
         this.item.score = result.score;
         this.item.maxScore = result.maxScore;
      }
      return result;
   }

   toJSON() {
      return Object.assign({}, { item: this.item });
   }

   getFriendlyValue() {
      throw new InvalidOperationException('Subclasses must implement a getFriendlyValue method');
   }
}

module.exports = BaseEntry;
